import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContacts, createContact, updateContact, deleteContact, setPage, setItemsPerPage } from '../store/slices/contactSlice';
import { fetchContactCategories } from '../store/slices/contactCategorySlice';
import { toast } from 'react-toastify';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, EnvelopeIcon, ArrowDownTrayIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { INSTITUTION_TYPES } from '../utils/constants';
import CountrySelect from '../components/CountrySelect';
import ReactCountryFlag from 'react-country-flag';
import { getCountryCode } from '../utils/countries';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination from '../components/Pagination';
import UserSearchSelect from '../components/UserSearchSelect';
import api from '../utils/api';

const PRIORITY_LEVELS = ['High', 'Medium', 'Low'];
const RELATIONSHIP_STAGES = ['Cold', 'Introduced', 'In discussion', 'MoU signed', 'Active collaboration', 'Dormant'];
const GOV_PRIVATE = ['Government', 'Private', 'Public', 'N/A'];

// Helper function to get priority badge color
const getPriorityBadgeColor = (priority) => {
  const colors = {
    'High': 'bg-red-100 text-red-800 border-red-200',
    'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Low': 'bg-green-100 text-green-800 border-green-200'
  };
  return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
};

// Helper function to get relationship stage badge color
const getRelationshipStageBadgeColor = (stage) => {
  const colors = {
    'Cold': 'bg-blue-100 text-blue-800 border-blue-200',
    'Introduced': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    'In discussion': 'bg-purple-100 text-purple-800 border-purple-200',
    'MoU signed': 'bg-orange-100 text-orange-800 border-orange-200',
    'Active collaboration': 'bg-green-100 text-green-800 border-green-200',
    'Dormant': 'bg-gray-100 text-gray-800 border-gray-200'
  };
  return colors[stage] || 'bg-gray-100 text-gray-800 border-gray-200';
};

const InstitutionContacts = () => {
  const { institutionType } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    items,
    loading,
    total,
    currentPage,
    itemsPerPage,
    totalPages
  } = useSelector((state) => state.contacts);
  const { items: categories } = useSelector((state) => state.contactCategories);
  const { user } = useSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [viewingContact, setViewingContact] = useState(null);
  const [contactNumberInputs, setContactNumberInputs] = useState(['']);
  const [priorityFilter, setPriorityFilter] = useState('');
  const [relationshipStageFilter, setRelationshipStageFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [blockCodeFilter, setBlockCodeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, contact: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailSelectionModal, setShowEmailSelectionModal] = useState(false);
  const [emailSelectionContact, setEmailSelectionContact] = useState(null);
  const [selectedEmailsForContact, setSelectedEmailsForContact] = useState([]);

  // Convert URL param back to institution type name
  const institutionTypeName = INSTITUTION_TYPES.find(type =>
    type && type.toLowerCase().replace(/[^a-z0-9]/g, '-') === institutionType
  ) || '';



  // Check if user has access to this institution type - superadmin gets all access
  const hasAccess = user?.role === 'superadmin' ? true :
    (user?.institutionTypes && user.institutionTypes.includes(institutionTypeName));

  // Check if user can edit/delete 
  const canEdit = ['superadmin', 'global_admin_hq', 'country_director', 'institutional_relations_officer'].includes(user?.role);
  const canDelete = user?.canDeleteContacts === true;

  // Check if user can create new contacts
  const canCreate = true; // All users can create for now

  useEffect(() => {
    if (hasAccess) {
      const filters = {
        page: currentPage,
        limit: itemsPerPage,
        institutionType: institutionTypeName
      };

      if (priorityFilter) filters.priority = priorityFilter;
      if (relationshipStageFilter) filters.relationshipStage = relationshipStageFilter;
      if (countryFilter) filters.country = countryFilter;
      if (categoryFilter) filters.categoryId = categoryFilter;
      if (userFilter) filters.createdBy = userFilter;
      if (blockCodeFilter) filters.blockCode = blockCodeFilter;
      if (searchQuery) filters.search = searchQuery;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;

      dispatch(fetchContacts(filters));
    }
  }, [dispatch, hasAccess, currentPage, itemsPerPage, priorityFilter, relationshipStageFilter, countryFilter, categoryFilter, userFilter, blockCodeFilter, searchQuery, dateFrom, dateTo, institutionTypeName]);

  useEffect(() => {
    // Fetch categories for the dropdown
    dispatch(fetchContactCategories());
  }, [dispatch]);

  // Filter contacts by institution type
  const institutionContacts = (items || []).filter(contact =>
    contact && contact.institutionType === institutionTypeName
  );

  // Use filtered contacts directly (backend already handles other filters)
  const filteredContacts = institutionContacts;

  // Pagination handlers
  const handlePageChange = (page) => {
    dispatch(setPage(page));
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    dispatch(setItemsPerPage(newItemsPerPage));
  };

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      dispatch(setPage(1));
    }
  }, [priorityFilter, relationshipStageFilter, countryFilter, categoryFilter, userFilter, blockCodeFilter]);

  const [formData, setFormData] = useState({
    primaryName: '',
    name: '',
    emails: [''],
    selectedEmails: [],
    contactNumbers: [''],
    contactIndividual: '',
    country: '',
    region: '',
    city: '',
    website: '',
    institutionType: institutionTypeName,
    categoryId: '',
    customCategoryType: '',
    govPrivate: '',
    otherInfo: '',
    contactedOn: '',
    contactedBy: '',
    contactedTo: '',
    institutionName: '',
    designation: '',
    department: '',
    blockCode: '',
    priority: 'Medium',
    relationshipStage: 'Cold'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const validEmails = formData.emails.filter(email => email.trim() !== '');
      const contactData = {
        ...formData,
        emails: validEmails,
        selectedEmails: formData.selectedEmails.filter(email => validEmails.includes(email)),
        contactNumbers: contactNumberInputs.filter(number => number.trim() !== ''),
        institutionType: institutionTypeName
      };

      if (editingContact) {
        await dispatch(updateContact({ id: editingContact.id, data: contactData })).unwrap();
        toast.success('Contact updated successfully');
      } else {
        await dispatch(createContact(contactData)).unwrap();
        toast.success('Contact created successfully');
      }

      setShowModal(false);
      resetForm();
    } catch (err) {
      toast.error(err.message || 'Failed to save contact');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      primaryName: contact.primaryName || '',
      name: contact.name || '',
      emails: contact.emails && contact.emails.length > 0 ? contact.emails : [''],
      selectedEmails: contact.selectedEmails || [],
      contactNumbers: contact.contactNumbers && contact.contactNumbers.length > 0 ? contact.contactNumbers : [''],
      contactIndividual: contact.contactIndividual || '',
      country: contact.country || '',
      region: contact.region || '',
      city: contact.city || '',
      website: contact.website || '',
      institutionType: contact.institutionType || institutionTypeName,
      categoryId: contact.categoryId || '',
      customCategoryType: contact.customCategoryType || '',
      govPrivate: contact.govPrivate || '',
      otherInfo: contact.otherInfo || '',
      contactedOn: contact.contactedOn ? contact.contactedOn.split('T')[0] : '',
      contactedBy: contact.contactedBy || '',
      contactedTo: contact.contactedTo || '',
      institutionName: contact.institutionName || '',
      designation: contact.designation || '',
      department: contact.department || '',
      blockCode: contact.blockCode || '',
      priority: contact.priority || 'Medium',
      relationshipStage: contact.relationshipStage || 'Cold'
    });
    setContactNumberInputs(contact.contactNumbers && contact.contactNumbers.length > 0 ? contact.contactNumbers : ['']);
    setShowModal(true);
  };

  const handleDelete = (contact) => {
    setDeleteDialog({ isOpen: true, contact });
  };

  const handleSendEmail = (contact) => {
    // Navigate to compose email page with contact pre-selected
    navigate(`/compose-email?contactId=${contact.id}`);
  };

  // Email Selection Modal Handlers
  const handleOpenEmailSelection = (contact) => {
    setEmailSelectionContact(contact);
    setSelectedEmailsForContact(contact.selectedEmails || []);
    setShowEmailSelectionModal(true);
  };

  const handleToggleEmailSelection = (email) => {
    setSelectedEmailsForContact(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  const handleSaveEmailSelection = async () => {
    if (!emailSelectionContact) return;

    try {
      await dispatch(updateContact({
        id: emailSelectionContact.id,
        data: {
          ...emailSelectionContact,
          selectedEmails: selectedEmailsForContact
        }
      })).unwrap();

      toast.success('Email selection updated successfully');
      setShowEmailSelectionModal(false);
      setEmailSelectionContact(null);
      setSelectedEmailsForContact([]);
    } catch (err) {
      toast.error('Failed to update email selection');
    }
  };

  const confirmDelete = async () => {
    if (deleteDialog.contact) {
      try {
        await dispatch(deleteContact(deleteDialog.contact.id)).unwrap();
        toast.success('Contact deleted successfully');
        setDeleteDialog({ isOpen: false, contact: null });
      } catch (err) {
        toast.error('Failed to delete contact');
        setDeleteDialog({ isOpen: false, contact: null });
      }
    }
  };

  const cancelDelete = () => {
    setDeleteDialog({ isOpen: false, contact: null });
  };

  // Export functions
  const [exportingMyContacts, setExportingMyContacts] = useState(false);
  const [exportingAllContacts, setExportingAllContacts] = useState(false);

  const handleExportMyContacts = async () => {
    try {
      setExportingMyContacts(true);
      const response = await api.get('/contacts/export', {
        params: {
          institutionType: institutionTypeName,
          myContactsOnly: 'true'
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${institutionTypeName}_MyContacts_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Your contacts exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to export data');
      } else {
        toast.error('Failed to export contacts');
      }
    } finally {
      setExportingMyContacts(false);
    }
  };

  const handleExportAllContacts = async () => {
    try {
      setExportingAllContacts(true);
      const response = await api.get('/contacts/export', {
        params: {
          institutionType: institutionTypeName,
          myContactsOnly: 'false'
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${institutionTypeName}_AllContacts_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('All contacts exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to export data');
      } else {
        toast.error('Failed to export contacts');
      }
    } finally {
      setExportingAllContacts(false);
    }
  };

  const resetForm = () => {
    setEditingContact(null);
    setFormData({
      primaryName: '',
      name: '',
      emails: [''],
      selectedEmails: [],
      contactNumbers: [''],
      contactIndividual: '',
      country: '',
      region: '',
      city: '',
      website: '',
      institutionType: institutionTypeName,
      categoryId: '',
      customCategoryType: '',
      govPrivate: '',
      otherInfo: '',
      contactedOn: '',
      contactedBy: '',
      contactedTo: '',
      institutionName: '',
      designation: '',
      department: '',
      blockCode: '',
      priority: 'Medium',
      relationshipStage: 'Cold'
    });
    setContactNumberInputs(['']);
  };

  const addEmailField = () => {
    setFormData({ ...formData, emails: [...formData.emails, ''] });
  };

  const removeEmailField = (index) => {
    const newEmails = formData.emails.filter((_, i) => i !== index);
    setFormData({ ...formData, emails: newEmails.length > 0 ? newEmails : [''] });
  };

  const updateEmail = (index, value) => {
    const newEmails = [...formData.emails];
    newEmails[index] = value;
    setFormData({ ...formData, emails: newEmails });
  };

  const toggleEmailForSending = (email) => {
    setFormData(prev => {
      const selectedEmails = prev.selectedEmails || [];
      if (selectedEmails.includes(email)) {
        return { ...prev, selectedEmails: selectedEmails.filter(e => e !== email) };
      } else {
        return { ...prev, selectedEmails: [...selectedEmails, email] };
      }
    });
  };

  const addContactNumberField = () => {
    setContactNumberInputs([...contactNumberInputs, '']);
  };

  const removeContactNumberField = (index) => {
    const newNumbers = contactNumberInputs.filter((_, i) => i !== index);
    setContactNumberInputs(newNumbers.length > 0 ? newNumbers : ['']);
  };

  const updateContactNumber = (index, value) => {
    const newNumbers = [...contactNumberInputs];
    newNumbers[index] = value;
    setContactNumberInputs(newNumbers);
  };

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have access to {institutionTypeName} contacts.</p>
        </div>
      </div>
    );
  }

  if (!institutionTypeName) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Institution Type Not Found</h2>
          <p className="text-gray-600">The requested institution type could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        {/* Compact Header with Actions */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Title and Count */}
            <div className="flex items-center gap-3">
              <h1 className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {institutionTypeName}
              </h1>
              <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-semibold">
                {total || 0} contacts
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {user?.canExportExcel && (
                <>
                  <button
                    onClick={handleExportMyContacts}
                    disabled={exportingMyContacts}
                    className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Export contacts added by you"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                    {exportingMyContacts ? 'Exporting...' : 'My Contacts'}
                  </button>
                  <button
                    onClick={handleExportAllContacts}
                    disabled={exportingAllContacts}
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Export all contacts in this institution type"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                    {exportingAllContacts ? 'Exporting...' : 'All Contacts'}
                  </button>
                </>
              )}
              {canCreate && (
                <button
                  onClick={() => setShowModal(true)}
                  className="flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all text-sm font-medium shadow-md"
                >
                  <PlusIcon className="h-4 w-4 mr-1.5" />
                  Add Contact
                </button>
              )}
            </div>
          </div>

          {/* Compact Filters Section */}
          <div className="mt-4 space-y-3">
            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Organization or Contact Person..."
                className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Priority:</label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                >
                  <option value="">All Priorities</option>
                  {PRIORITY_LEVELS.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Relationship:</label>
                <select
                  value={relationshipStageFilter}
                  onChange={(e) => setRelationshipStageFilter(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                >
                  <option value="">All Stages</option>
                  {RELATIONSHIP_STAGES.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-2 sm:col-span-1 flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Country:</label>
                <CountrySelect
                  value={countryFilter}
                  onChange={(countryName) => setCountryFilter(countryName)}
                  placeholder="All Countries"
                  isClearable={true}
                  className="text-xs"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Category:</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                >
                  <option value="">All Categories</option>
                  {categories.filter(cat => cat.isActive).map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Block Code:</label>
                <input
                  type="text"
                  value={blockCodeFilter}
                  onChange={(e) => setBlockCodeFilter(e.target.value)}
                  placeholder="Search block code..."
                  className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">From Date:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">To Date:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                />
              </div>

              {user?.role === 'superadmin' && (
                <div className="col-span-2 sm:col-span-1 flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-600">Added By:</label>
                  <UserSearchSelect
                    value={userFilter}
                    onChange={setUserFilter}
                    placeholder="Search user..."
                  />
                </div>
              )}

              {(dateFrom || dateTo) && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-transparent">Clear</label>
                  <button
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                    }}
                    className="px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium border border-red-200"
                  >
                    Clear Dates
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Organization</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Contact Person</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Institution</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Designation</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Department</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Block Code</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Priority</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Relationship Stage</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Country</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Website</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Emails</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider sticky right-0 bg-gradient-to-r from-gray-50 to-gray-100 shadow-lg">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredContacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-blue-50 transition-colors duration-150">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{contact.primaryName}</div>
                        {contact.name && <div className="text-sm text-gray-600">{contact.name}</div>}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {contact.contactIndividual || '-'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {contact.institutionName || '-'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {contact.designation || '-'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {contact.department || '-'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {contact.blockCode || '-'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full border ${getPriorityBadgeColor(contact.priority || 'Medium')}`}>
                        {contact.priority || 'Medium'}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full border ${getRelationshipStageBadgeColor(contact.relationshipStage || 'Cold')}`}>
                        {contact.relationshipStage || 'Cold'}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center">
                        <ReactCountryFlag
                          countryCode={contact.country ? (getCountryCode(contact.country) || 'XX') : 'XX'}
                          svg
                          style={{
                            width: '1.2em',
                            height: '1.2em',
                            marginRight: '8px'
                          }}
                        />
                        {contact.country || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {contact.website ? (
                        <a
                          href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline font-medium"
                        >
                          {contact.website}
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-gray-600">
                      {contact.emails && contact.emails.length > 0 ? (
                        <div className="space-y-1">
                          {contact.emails.slice(0, 2).map((email, idx) => (
                            <div key={idx} className="text-xs font-medium">{email}</div>
                          ))}
                          {contact.emails.length > 2 && (
                            <div className="text-xs text-gray-400 font-medium">+{contact.emails.length - 2} more</div>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-white hover:bg-blue-50 shadow-lg border-l border-gray-200">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setViewingContact(contact)}
                          className="text-green-600 hover:text-green-800 hover:bg-green-50 p-2 rounded-lg transition-all duration-200"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {contact.emails && contact.emails.length > 0 && (
                          <button
                            onClick={() => handleOpenEmailSelection(contact)}
                            className="text-orange-600 hover:text-orange-800 hover:bg-orange-50 p-2 rounded-lg transition-all duration-200 relative"
                            title="Select Emails for Sending"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                            {contact.selectedEmails && contact.selectedEmails.length > 0 && (
                              <span className="absolute -top-1 -right-1 bg-orange-600 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                                {contact.selectedEmails.length}
                              </span>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleSendEmail(contact)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-lg transition-all duration-200"
                          title="Send Email"
                        >
                          <EnvelopeIcon className="h-5 w-5" />
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => handleEdit(contact)}
                            className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 p-2 rounded-lg transition-all duration-200"
                            title="Edit Contact"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(contact)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-all duration-200"
                            title="Delete Contact"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {institutionContacts.length === 0 && (
              <div className="text-center py-16">
                <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PlusIcon className="h-10 w-10 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium mb-4">No {institutionTypeName ? institutionTypeName.toLowerCase() : 'institution'} contacts found.</p>
                <button
                  onClick={() => setShowModal(true)}
                  className="text-blue-600 hover:text-blue-700 font-semibold"
                >
                  Add your first contact
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={total}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {/* Add/Edit Contact Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={() => setShowModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full mx-4 sm:mx-0">
              <form onSubmit={handleSubmit}>
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-4 sm:py-5">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    {editingContact ? `Edit ${institutionTypeName} Contact` : `Add New ${institutionTypeName} Contact`}
                  </h3>
                </div>

                <div className="bg-white px-4 sm:px-6 py-4 sm:py-6 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Organization Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.primaryName}
                        onChange={(e) => setFormData({ ...formData, primaryName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Primary organization name"
                      />
                    </div>

                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Alternative or full name"
                      />
                    </div> */}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                      <input
                        type="text"
                        value={formData.contactIndividual}
                        onChange={(e) => setFormData({ ...formData, contactIndividual: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Individual contact person"
                      />
                    </div>

                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Institution Name</label>
                      <input
                        type="text"
                        value={formData.institutionName}
                        onChange={(e) => setFormData({ ...formData, institutionName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Name of the institution/organization"
                      />
                    </div> */}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Designation / Title</label>
                      <input
                        type="text"
                        value={formData.designation}
                        onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Job title or designation"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Department within the institution"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Block Code</label>
                      <input
                        type="text"
                        value={formData.blockCode}
                        onChange={(e) => setFormData({ ...formData, blockCode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter block code"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                      <CountrySelect
                        value={formData.country}
                        onChange={(countryName) => {
                          setFormData({ ...formData, country: countryName });
                        }}
                        placeholder="Select country..."
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Region/State</label>
                      <input
                        type="text"
                        value={formData.region}
                        onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter region or state"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter city"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="https://example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Institution Type</label>
                      <input
                        type="text"
                        value={institutionTypeName}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select
                        value={formData.categoryId}
                        onChange={(e) => {
                          setFormData({ ...formData, categoryId: e.target.value });
                          // Clear custom type if not "Others"
                          const selectedCategory = categories.find(cat => cat.id === parseInt(e.target.value));
                          if (selectedCategory?.name !== 'Others') {
                            setFormData(prev => ({ ...prev, customCategoryType: '' }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select category (optional)</option>
                        {categories.filter(cat => cat.isActive).map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>

                      {/* Show custom type input when "Others" is selected */}
                      {categories.find(cat => cat.id === parseInt(formData.categoryId))?.name === 'Others' && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Custom Category Type *</label>
                          <input
                            type="text"
                            value={formData.customCategoryType}
                            onChange={(e) => setFormData({ ...formData, customCategoryType: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter custom category type (e.g., Sports, Music, etc.)"
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Specify the custom category type for this contact
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Priority Level</label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {PRIORITY_LEVELS.map(priority => (
                          <option key={priority} value={priority}>{priority}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Relationship Stage</label>
                      <select
                        value={formData.relationshipStage}
                        onChange={(e) => setFormData({ ...formData, relationshipStage: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {RELATIONSHIP_STAGES.map(stage => (
                          <option key={stage} value={stage}>{stage}</option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Addresses</label>
                      <p className="text-xs text-gray-500 mb-3">Check the emails you want to use for sending campaigns</p>
                      {formData.emails.map((email, index) => (
                        <div key={index} className="flex gap-2 mb-2 items-center">
                          {email.trim() && (
                            <input
                              type="checkbox"
                              checked={formData.selectedEmails.includes(email)}
                              onChange={() => toggleEmailForSending(email)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              title="Select for sending emails"
                            />
                          )}
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => updateEmail(index, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="email@example.com"
                          />
                          {formData.emails.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeEmailField(index)}
                              className="px-3 py-2 text-red-600 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addEmailField}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        + Add another email
                      </button>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contact Numbers</label>
                      {contactNumberInputs.map((number, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="tel"
                            value={number}
                            onChange={(e) => updateContactNumber(index, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="+1 (555) 123-4567"
                          />
                          {contactNumberInputs.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeContactNumberField(index)}
                              className="px-3 py-2 text-red-600 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addContactNumberField}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        + Add another contact number
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sector</label>
                      <select
                        value={formData.govPrivate}
                        onChange={(e) => setFormData({ ...formData, govPrivate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select sector</option>
                        {GOV_PRIVATE.map(gp => (
                          <option key={gp} value={gp}>{gp}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contacted On</label>
                      <input
                        type="date"
                        value={formData.contactedOn}
                        onChange={(e) => setFormData({ ...formData, contactedOn: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contacted By</label>
                      <input
                        type="text"
                        value={formData.contactedBy}
                        onChange={(e) => setFormData({ ...formData, contactedBy: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Person who made the contact"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contacted To</label>
                      <input
                        type="text"
                        value={formData.contactedTo}
                        onChange={(e) => setFormData({ ...formData, contactedTo: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Person who was contacted"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                      <textarea
                        value={formData.otherInfo}
                        onChange={(e) => setFormData({ ...formData, otherInfo: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Additional notes or description"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>{editingContact ? 'Updating...' : 'Creating...'}</span>
                      </>
                    ) : (
                      editingContact ? 'Update Contact' : 'Create Contact'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="w-full sm:w-auto mt-3 sm:mt-0 px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Contact Modal */}
      {viewingContact && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setViewingContact(null)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Details</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Organization Name</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingContact.primaryName}</p>
                  </div>

                  {viewingContact.name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Secondary Name</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.name}</p>
                    </div>
                  )}

                  {viewingContact.contactIndividual && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.contactIndividual}</p>
                    </div>
                  )}

                  {viewingContact.institutionName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Institution Name</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.institutionName}</p>
                    </div>
                  )}

                  {viewingContact.designation && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Designation / Title</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.designation}</p>
                    </div>
                  )}

                  {viewingContact.department && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Department</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.department}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Priority Level</label>
                    <span className={`mt-1 px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getPriorityBadgeColor(viewingContact.priority || 'Medium')}`}>
                      {viewingContact.priority || 'Medium'}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Relationship Stage</label>
                    <span className={`mt-1 px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRelationshipStageBadgeColor(viewingContact.relationshipStage || 'Cold')}`}>
                      {viewingContact.relationshipStage || 'Cold'}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <div className="mt-1 flex items-center">
                      <ReactCountryFlag
                        countryCode={viewingContact.country ? (getCountryCode(viewingContact.country) || 'XX') : 'XX'}
                        svg
                        style={{
                          width: '1.2em',
                          height: '1.2em',
                          marginRight: '8px'
                        }}
                      />
                      <p className="text-sm text-gray-900">{viewingContact.country || 'Unknown'}</p>
                    </div>
                  </div>

                  {viewingContact.region && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Region/State</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.region}</p>
                    </div>
                  )}

                  {viewingContact.city && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.city}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Institution Type</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingContact.institutionType}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContact.contactCategory?.name || 'None'}
                    </p>
                  </div>

                  {viewingContact.customCategoryType && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Custom Category Type</label>
                      <p className="mt-1 text-sm text-gray-900">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          {viewingContact.customCategoryType}
                        </span>
                      </p>
                    </div>
                  )}

                  {viewingContact.website && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Website</label>
                      <p className="mt-1 text-sm text-gray-900">
                        <a
                          href={viewingContact.website.startsWith('http') ? viewingContact.website : `https://${viewingContact.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {viewingContact.website}
                        </a>
                      </p>
                    </div>
                  )}

                  {viewingContact.govPrivate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Sector</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.govPrivate}</p>
                    </div>
                  )}

                  {viewingContact.contactedOn && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contacted On</label>
                      <p className="mt-1 text-sm text-gray-900">{new Date(viewingContact.contactedOn).toLocaleDateString()}</p>
                    </div>
                  )}

                  {viewingContact.contactedBy && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contacted By</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.contactedBy}</p>
                    </div>
                  )}

                  {viewingContact.contactedTo && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contacted To</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.contactedTo}</p>
                    </div>
                  )}

                  {viewingContact.emails && viewingContact.emails.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Email Addresses</label>
                      <div className="mt-1 space-y-1">
                        {viewingContact.emails.map((email, idx) => (
                          <p key={idx} className="text-sm text-gray-900">{email}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewingContact.contactNumbers && viewingContact.contactNumbers.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Contact Numbers</label>
                      <div className="mt-1 space-y-1">
                        {viewingContact.contactNumbers.map((number, idx) => (
                          <p key={idx} className="text-sm text-gray-900">
                            <a
                              href={`tel:${number}`}
                              className="text-blue-600 hover:text-blue-800 underline"
                              title={`Call ${number}`}
                            >
                              {number}
                            </a>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewingContact.otherInfo && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Additional Information</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.otherInfo}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setViewingContact(null)}
                  className="w-full sm:w-auto px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Contact"
        message={`Are you sure you want to delete "${deleteDialog.contact?.primaryName || deleteDialog.contact?.name || 'this contact'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Email Selection Modal */}
      {showEmailSelectionModal && emailSelectionContact && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={() => setShowEmailSelectionModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-6 py-5">
                <h3 className="text-xl font-bold text-white">Select Emails for Sending</h3>
                <p className="text-sm text-orange-100 mt-1">{emailSelectionContact.primaryName}</p>
              </div>

              <div className="bg-white px-6 py-6">
                <p className="text-sm text-gray-600 mb-4">
                  Select which email addresses should be used when sending emails to this contact. You can select one or multiple emails.
                </p>

                {emailSelectionContact.emails && emailSelectionContact.emails.length > 0 ? (
                  <div className="space-y-3">
                    {emailSelectionContact.emails.map((email, index) => (
                      <label
                        key={index}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                          selectedEmailsForContact.includes(email)
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEmailsForContact.includes(email)}
                          onChange={() => handleToggleEmailSelection(email)}
                          className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">{email}</span>
                        {selectedEmailsForContact.includes(email) && (
                          <CheckCircleIcon className="ml-auto h-5 w-5 text-orange-600" />
                        )}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <EnvelopeIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No email addresses available for this contact.</p>
                  </div>
                )}

                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <p className="text-sm text-blue-800 font-medium">
                        Selected: {selectedEmailsForContact.length} email{selectedEmailsForContact.length !== 1 ? 's' : ''}
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        These emails will be used when sending campaigns or individual emails to this contact.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={handleSaveEmailSelection}
                  className="w-full sm:w-auto px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold"
                >
                  Save Selection
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailSelectionModal(false);
                    setEmailSelectionContact(null);
                    setSelectedEmailsForContact([]);
                  }}
                  className="w-full sm:w-auto mt-3 sm:mt-0 px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstitutionContacts;